// client/src/components/event/ActionPostButton.jsx
/**
 * "Post to socials" for an event item action's setup screen (raffle, auction,
 * challenge, ...). Opens the same Post Now / Schedule choice as the item page,
 * then hands off to MediaPost / SchedulePost with the item's photos and a
 * caption built from the action's details + public link. Those composers
 * already handle platform selection, approvals and the event/item tagging.
 */

import React, { useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PostChoiceModal from "../media/PostChoiceModal";
import { buildActionCaption } from "../../utils/actionPostCaption";

// Server returns /tenants/... paths; the composers expect the /public mount.
const toPublic = (p) =>
  !p ? p : p.startsWith("/public/") ? p : p.startsWith("/tenants/") ? `/public${p}` : p;

const ActionPostButton = ({
  tenantId,
  eventId,
  itemId,
  actionType,
  config,
  itemName,
  link,
  tiers,
  className = "copy-link-button",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const baseUrl = process.env.REACT_APP_BASE_URL || "";

  const go = async (target) => {
    setOpen(false);
    setBusy(true);
    let images = [];
    try {
      const { data } = await axios.get(
        `${baseUrl}/api/events/${tenantId}/events/${eventId}/items/${itemId}/images`
      );
      images = (data?.images || []).map((i) => toPublic(i?.path || i?.url || i)).filter(Boolean);
    } catch (err) {
      // Non-fatal: the composer still opens with the caption; media can be added there.
      console.warn("Could not load item images for action post:", err?.message || err);
    }
    const name = config?.name || itemName || "";
    navigate(`/${tenantId}/${eventId}/${itemId}/${target}`, {
      state: {
        context: "event-action",
        actionType,
        tenant: tenantId,
        eventId,
        itemId,
        title: name,
        content: buildActionCaption({ type: actionType, config, name: itemName, link, tiers }),
        image: images[0] || "",
        images,
        videos: [],
      },
    });
  };

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={() => setOpen(true)}
        disabled={busy}
        title={t("events.actionPost.hint", "Post this to your connected accounts")}
      >
        {busy ? t("events.actionPost.opening", "Opening…") : t("events.actionPost.button", "📣 Post to socials")}
      </button>
      {/* Portal + own stacking context: the setup forms live inside a
          .modal-overlay (z-index 1200), above PostChoiceModal's 1050. */}
      {open && createPortal(
        <div style={{ position: "relative", zIndex: 1300 }}>
          <PostChoiceModal
            isOpen
            onClose={() => setOpen(false)}
            title={t("events.actionPost.title", "Post to socials")}
            hint={t("events.actionPost.choiceHint", "The item's photos and this action's details will be filled in. You can edit everything before it goes out.")}
            onPostNow={() => go("mediaPost")}
            onSchedule={() => go("schedulePost")}
          />
        </div>,
        document.body
      )}
    </>
  );
};

export default ActionPostButton;
